import { z } from 'zod';
import { cursorPagination, isoDate, latitude, longitude } from './schemas.js';
import { FORM_SCOPES, formSchemaSchema, scoringSchema } from './form/types.js';

/* --------------------------------------------------------------- แบบฟอร์ม */

export const createFormSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, 'code ต้องเป็นตัวพิมพ์ใหญ่ ตัวเลข และ _'),
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).optional(),
  scope: z.enum(FORM_SCOPES),
  schema: formSchemaSchema,
  scoring: scoringSchema.optional(),
});

export const updateFormSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(1000).optional(),
  schema: formSchemaSchema.optional(),
  scoring: scoringSchema.nullable().optional(),
});

export const listFormsQuerySchema = z.object({
  scope: z.enum(FORM_SCOPES).optional(),
  status: z.enum(['draft', 'published', 'archived']).optional(),
});

/* ------------------------------------------------------------- การลงพื้นที่ */

export const VISIT_TYPES = [
  'intake',
  'followup',
  'emergency',
  'verification',
  'disbursement',
] as const;

export const VISIT_STATUSES = [
  'scheduled',
  'in_progress',
  'completed',
  'missed',
  'cancelled',
  'rescheduled',
] as const;

export const VISIT_OUTCOMES = [
  'improved',
  'stable',
  'worsened',
  'not_found',
  'refused',
  'other',
] as const;

export const visitTypeEnum = z.enum(VISIT_TYPES);
export const visitStatusEnum = z.enum(VISIT_STATUSES);
export const visitOutcomeEnum = z.enum(VISIT_OUTCOMES);

export const VISIT_TYPE_LABEL_TH: Record<(typeof VISIT_TYPES)[number], string> = {
  intake: 'สำรวจแรกรับ',
  followup: 'ติดตามผล',
  emergency: 'ฉุกเฉิน',
  verification: 'ตรวจสอบข้อมูล',
  disbursement: 'ส่งมอบความช่วยเหลือ',
};

export const VISIT_STATUS_LABEL_TH: Record<(typeof VISIT_STATUSES)[number], string> = {
  scheduled: 'นัดหมายแล้ว',
  in_progress: 'กำลังดำเนินการ',
  completed: 'เสร็จสิ้น',
  missed: 'ไม่ได้ไป',
  cancelled: 'ยกเลิก',
  rescheduled: 'เลื่อนนัด',
};

export const VISIT_OUTCOME_LABEL_TH: Record<(typeof VISIT_OUTCOMES)[number], string> = {
  improved: 'ดีขึ้น',
  stable: 'คงเดิม',
  worsened: 'แย่ลง',
  not_found: 'ไม่พบผู้รับความช่วยเหลือ',
  refused: 'ปฏิเสธการเยี่ยม',
  other: 'อื่น ๆ',
};

export const LOCATION_FLAG_LABEL_TH: Record<string, string> = {
  ok: 'อยู่ในพื้นที่',
  far: 'อยู่ห่างจากบ้าน',
  no_gps: 'ไม่มีสัญญาณ GPS',
  mocked: 'สงสัยตำแหน่งปลอม',
};

export const createVisitSchema = z.object({
  beneficiaryId: z.string().min(1).max(40),
  staffId: z.string().min(1).max(40).optional(),
  visitType: visitTypeEnum.default('followup'),
  scheduledAt: z.string().datetime({ offset: true }).optional(),
  formCode: z.string().max(40).optional(),
  note: z.string().trim().max(1000).optional(),
});

export const listVisitsQuerySchema = cursorPagination.extend({
  staffId: z.string().max(40).optional(),
  beneficiaryId: z.string().max(40).optional(),
  status: visitStatusEnum.optional(),
  visitType: visitTypeEnum.optional(),
  flagged: z.enum(['1', '0']).optional(),
  from: z.string().max(40).optional(),
  to: z.string().max(40).optional(),
});

export const checkinSchema = z.object({
  lat: latitude.optional(),
  lng: longitude.optional(),
  accuracy: z.number().min(0).max(100_000).optional(),
  isMocked: z.boolean().optional(),
});

export const visitPhotoSchema = z.object({
  key: z.string().min(1).max(255),
  fieldKey: z.string().max(60).optional(),
  caption: z.string().trim().max(300).optional(),
  takenAt: z.string().datetime({ offset: true }).optional(),
  lat: latitude.optional(),
  lng: longitude.optional(),
  width: z.number().int().min(1).max(20_000).optional(),
  height: z.number().int().min(1).max(20_000).optional(),
  sizeBytes: z.number().int().min(0).max(50_000_000).optional(),
  sha256: z.string().length(64).optional(),
});

export const submitVisitSchema = z.object({
  formData: z.record(z.string(), z.unknown()).default({}),
  photos: z.array(visitPhotoSchema).max(40).default([]),
  signatureKey: z.string().max(255).optional(),
  summary: z.string().trim().max(4000).optional(),
  nextAction: z.string().trim().max(1000).optional(),
  outcome: visitOutcomeEnum.optional(),
  /** บังคับเมื่อ location_flag ไม่ใช่ ok */
  locationReason: z.string().trim().max(500).optional(),
  /** จำนวนวันก่อนนัดครั้งถัดไป — ไม่ส่ง = ใช้ค่าจาก followup_schedule */
  nextVisitDays: z.number().int().min(1).max(730).optional(),
});

export const rescheduleVisitSchema = z.object({
  scheduledAt: z.string().datetime({ offset: true }),
  reason: z.string().trim().min(1).max(500),
});

export const cancelVisitSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export const reviewVisitSchema = z.object({
  approved: z.boolean(),
  note: z.string().trim().max(1000).optional(),
});

/* ------------------------------------------------------------ ตารางนัดติดตาม */

export const createFollowupScheduleSchema = z.object({
  beneficiaryId: z.string().min(1).max(40),
  assignedStaffId: z.string().max(40).optional(),
  formCode: z.string().max(40).default('FOLLOWUP_GENERAL'),
  intervalDays: z.number().int().min(7).max(730),
  reminderOffsets: z.array(z.number().int().min(-30).max(0)).max(6).default([-3, -1, 0]),
  startDate: isoDate.optional(),
  endDate: isoDate.optional(),
});

export const updateFollowupScheduleSchema = createFollowupScheduleSchema
  .omit({ beneficiaryId: true })
  .partial()
  .extend({ isActive: z.boolean().optional() });

/* ------------------------------------------------------------------ uploads */

export const UPLOAD_PURPOSES = [
  'visit_photo',
  'signature',
  'document',
  'slip',
  'avatar',
  /** รูปประกอบคำขอความช่วยเหลือจากประชาชน — อัปโหลดก่อนมีเคสในระบบ */
  'help_request',
] as const;

export const presignSchema = z.object({
  purpose: z.enum(UPLOAD_PURPOSES),
  refId: z.string().max(40).optional(),
  count: z.number().int().min(1).max(20).default(1),
  contentType: z
    .string()
    .regex(/^(image\/(jpeg|png|webp)|application\/pdf)$/, 'รองรับเฉพาะ JPEG, PNG, WebP และ PDF')
    .default('image/jpeg'),
});

export type CreateVisitInput = z.infer<typeof createVisitSchema>;
export type SubmitVisitInput = z.infer<typeof submitVisitSchema>;
export type CheckinInput = z.infer<typeof checkinSchema>;
export type CreateFormInput = z.infer<typeof createFormSchema>;
export type PresignInput = z.infer<typeof presignSchema>;
